import { isKnownCountry, resolveToKnownCountry } from './countries';

// localStorage key used to remember the user's last country pick so
// repeat visitors don't have to re-select after the very first time.
// Kept here (not under `i18n`) so exporting the value from tests is
// trivial and so grepping for `wallawhats_phone_country` hits one
// place.
const STORAGE_KEY = 'wallawhats_phone_country';

// Cookie written by the CloudFront Function (frontend.yml) at viewer
// response time. It proxies the `CloudFront-Viewer-Country` header as
// a small, non-HttpOnly cookie so JS can read it. Non-sensitive.
const COUNTRY_COOKIE = 'wallawhats_country';

/**
 * Three-stage fallback chain for picking the initial country in
 * <PhoneInput />:
 *
 *   1. explicit saved preference (localStorage — set after the user
 *      picks a country on a previous visit)
 *   2. CloudFront geo cookie (set by viewer-response function from
 *      the CloudFront-Viewer-Country header)
 *   3. navigator.language suffix ("es-ES" → "ES", "en-GB" → "GB")
 *   4. default "US"
 *
 * SSR-safe — every browser-only access is guarded.
 */
export function resolveInitialCountry(): string {
  if (typeof window === 'undefined') return 'US';

  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    const resolved = resolveToKnownCountry(saved);
    if (resolved) return resolved;
  } catch {
    /* private mode / storage disabled */
  }

  // Cookie value comes straight from CloudFront-Viewer-Country so it
  // can be a territory ISO-2 (GP, HK, GL, …). resolveToKnownCountry
  // handles both direct hits and the territory→parent fallback.
  const fromCookie = resolveToKnownCountry(parseCountryCookie(document.cookie));
  if (fromCookie) return fromCookie;

  const fromLang = resolveToKnownCountry(parseNavigatorLanguage(navigator.language));
  if (fromLang) return fromLang;

  return 'US';
}

export function saveCountryPreference(iso2: string): void {
  if (typeof window === 'undefined') return;
  if (!isKnownCountry(iso2)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, iso2.toUpperCase());
  } catch {
    /* private mode / storage disabled — fine to ignore */
  }
}

/**
 * Parse `document.cookie` looking for the CloudFront geo cookie.
 * Returns the upper-cased ISO-2 when present and shaped like a
 * valid 2-letter code, null otherwise.
 */
export function parseCountryCookie(cookieHeader: string): string | null {
  if (!cookieHeader) return null;
  for (const raw of cookieHeader.split(';')) {
    const idx = raw.indexOf('=');
    if (idx < 0) continue;
    const name = raw.slice(0, idx).trim();
    if (name !== COUNTRY_COOKIE) continue;
    const value = raw.slice(idx + 1).trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(value)) return value;
    return null;
  }
  return null;
}

/**
 * `navigator.language` is shaped like "es-ES", "en-GB", "pt-BR".
 * The region suffix after the first dash is a valid ISO-3166-1
 * alpha-2 code. Returns null when the string is just a language
 * ("es", "ja") with no region — those are too ambiguous to guess.
 */
export function parseNavigatorLanguage(lang: string | undefined | null): string | null {
  if (!lang) return null;
  const m = lang.match(/^[a-z]{2,3}[-_]([A-Za-z]{2})(?:[-_]|$)/);
  if (!m) return null;
  return m[1].toUpperCase();
}
